/**
 * Single-device session enforcement.
 *
 * Each PWA install gets a persistent random `deviceId` stored in localStorage.
 * On sign-in we write `users/{uid}/deviceSessions/active` with the local
 * deviceId. We listen on that doc — if the remote deviceId stops matching
 * ours (i.e. another device signed in as the same user), this device is
 * forced to sign out.
 */

import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const DEVICE_ID_KEY = "the99.deviceId";
const ACTIVE_DOC = "active";

function generateId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {}
  return `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getOrCreateDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = generateId();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable — fall back to a transient id (won't survive
    // reload but at least doesn't crash).
    return generateId();
  }
}

function activeDocRef(uid) {
  return doc(db, "users", uid, "deviceSessions", ACTIVE_DOC);
}

/**
 * Claim the active session slot for this device. Other devices listening to
 * this doc will detect the mismatch and sign themselves out.
 */
export async function claimActiveDeviceSession(uid) {
  if (!uid) return null;
  const deviceId = getOrCreateDeviceId();
  const ua =
    typeof navigator !== "undefined" && typeof navigator.userAgent === "string"
      ? navigator.userAgent.slice(0, 240)
      : "";
  try {
    await setDoc(activeDocRef(uid), {
      deviceId,
      userAgent: ua,
      claimedAt: serverTimestamp(),
    });
    console.log("[deviceSession] claimed slot uid=%s deviceId=%s", uid, deviceId);
  } catch (err) {
    console.warn("[deviceSession] claim failed:", err);
  }
  return deviceId;
}

/**
 * Watch the active-session doc. Calls `onEvicted()` when the remote deviceId
 * differs from this device's deviceId. Returns an unsubscribe function.
 */
export function watchActiveDeviceSession(uid, onEvicted) {
  if (!uid) return () => {};
  const deviceId = getOrCreateDeviceId();
  let evicted = false;
  return onSnapshot(
    activeDocRef(uid),
    (snap) => {
      if (evicted) return;
      if (!snap.exists()) {
        console.log("[deviceSession] snap: doc does not exist yet");
        return;
      }
      const remote = snap.data() || {};
      const fromCache = snap.metadata?.fromCache;
      console.log(
        "[deviceSession] snap: remote=%s local=%s fromCache=%s",
        remote.deviceId,
        deviceId,
        fromCache
      );
      // Ignore cached snapshots — they may show stale data from before the
      // remote overwrite landed; we only act on confirmed server snapshots.
      if (fromCache) return;
      if (remote.deviceId && remote.deviceId !== deviceId) {
        evicted = true;
        console.warn(
          "[deviceSession] EVICTED — remote deviceId %s ≠ local %s",
          remote.deviceId,
          deviceId
        );
        try {
          onEvicted({ remoteDeviceId: remote.deviceId, userAgent: remote.userAgent || "" });
        } catch (err) {
          console.warn("[deviceSession] onEvicted handler threw:", err);
        }
      }
    },
    (err) => {
      console.warn("[deviceSession] listener error:", err);
    }
  );
}
